import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { SubmitProps } from './AttachmentForm'
import { allowedInputValueTypes } from './inputForm'
import { DecisionDialog, DialogResult } from './DecisionDialog'
import {
  addItemWithAttachments,
  saveAttachmentsToAddLater,
} from '@/checkout-instore-legacy/cart/actions'
import { useDispatch } from 'react-redux'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import { WithRouterProps } from 'react-router'
import {
  endCatalogStepEvent,
  logClickEvent,
} from '@/checkout-instore-legacy/utils/EventHelper'
import { AttachmentModal } from './AttachmentModal'
import { useAttachmentsContext } from './context/AttachmentsContext'
import isEmpty from 'lodash/isEmpty'
import { transformAttachmentsEmptyValues } from './utils'

export interface ItemAttachment {
  name: string
  content: Record<string, string>
}

interface Item {
  id: string
  quantity?: number
  manualPrice?: number | null
  attachments?: ItemAttachment[]
}

// TODO: this error popped up when moving the checkout-instore project
// to checkout-instore-legacy, but needs to be fixed at some point
// @ts-ignore
const Attachments = (props: WithRouterProps) => {
  const { location } = props

  const query = location
  const {
    showAttachments,
    attachments,
    skuId,
    manualPrice,
    quantity,
    setShowAttachments,
    customOnSubmit,
    beforeAddToCart,
  } = useAttachmentsContext()

  const dispatch = useDispatch()

  const [attachmentIsOpen, setAttachmentIsOpen] = useState(false)
  const [decisionDialogIsOpen, setDecisionDialogIsOpen] = useState(false)

  useEffect(() => {
    return () => {
      // set showAttachments state false when component unmount
      setShowAttachments(false)
    }
  }, [setShowAttachments])

  const attachmentsWithAllowedInputValues = useMemo(
    () =>
      attachments
        ? attachments
            ?.filter((attachment) => !attachment.composition)
            .map((attachment) => {
              return {
                ...attachment,
                inputValues: attachment.inputValues?.filter((inputValue) =>
                  allowedInputValueTypes.includes(inputValue.type)
                ),
              }
            })
        : [],
    [attachments]
  )

  const hasRequiredAttachments = useMemo(() => {
    return attachmentsWithAllowedInputValues.some((attachment) => {
      return attachment.required
    })
  }, [attachmentsWithAllowedInputValues])

  const hasAttachments = useMemo(
    () => !isEmpty(attachmentsWithAllowedInputValues),
    [attachmentsWithAllowedInputValues]
  )

  const emptyAttachments = useMemo(
    () => transformAttachmentsEmptyValues(attachmentsWithAllowedInputValues),
    [attachmentsWithAllowedInputValues]
  )

  useEffect(() => {
    if (showAttachments) {
      setDecisionDialogIsOpen(!hasRequiredAttachments)
      setAttachmentIsOpen(hasRequiredAttachments)
    } else {
      setDecisionDialogIsOpen(false)
      setAttachmentIsOpen(false)
    }
  }, [hasRequiredAttachments, showAttachments])

  const hasAssemblyItems = attachments?.some(
    (attachment) => attachment.composition
  )

  const addItemToCart = useCallback(
    (item: Item) => {
      logClickEvent('add-product-manually', 'Sale')
      endCatalogStepEvent()
      const metadata = {
        query,
      }

      if (hasAssemblyItems) {
        dispatch(saveAttachmentsToAddLater(item, metadata))
      } else {
        dispatch(addItemWithAttachments(item, metadata))
      }

      beforeAddToCart?.()
    },
    [hasAssemblyItems, dispatch, query, beforeAddToCart]
  )

  const onSubmitForm = useCallback(
    (formsValues: SubmitProps[]) => {
      if (skuId) {
        const item: Item = {
          id: skuId,
          manualPrice,
          quantity,
          attachments: formsValues.map((formValues) => {
            return {
              name: formValues.attachmentId,
              content: formValues.content,
            }
          }),
        }

        if (customOnSubmit) {
          customOnSubmit(item)
        } else {
          addItemToCart(item)
        }
      }
    },
    [addItemToCart, customOnSubmit, manualPrice, quantity, skuId]
  )

  const onCloseModal = useCallback(() => {
    setShowAttachments(false)
  }, [setShowAttachments])

  const handleDialogResult = (result: DialogResult) => {
    if (result === 'add-attachments') {
      setDecisionDialogIsOpen(false)
      setAttachmentIsOpen(true)
    } else if (result === 'skip-attachments') {
      if (skuId) {
        const item = {
          id: skuId,
          manualPrice,
          quantity,
          attachments: emptyAttachments,
        }
        if (customOnSubmit) {
          customOnSubmit(item)
        } else {
          addItemToCart(item)
        }
      }
    }
  }

  if (!hasAttachments) {
    return null
  }

  if (!showAttachments) {
    return null
  }

  return (
    <>
      <DecisionDialog
        data-testid="attachments-decision-dialog"
        isOpen={decisionDialogIsOpen}
        onClose={onCloseModal}
        handleResult={handleDialogResult}
      />

      <AttachmentModal
        data-testid="attachments-box"
        isOpen={attachmentIsOpen}
        onClose={onCloseModal}
        attachments={attachmentsWithAllowedInputValues}
        onSubmit={onSubmitForm}
      />
    </>
  )
}

export default withRouter(Attachments)
